import React, { useState, useEffect } from "react";
import {
  TextField,
  InputLabel,
  Box,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  FormLabel,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";
import OtpModal from "utility/otpModal";
import Swal from "sweetalert2";
import {
  useSendOtpToDoctorMutation,
  useRegisterDoctorMutation,
} from "slices/wellness/doctorSlice";

function Step1({ onNext }) {
  const [imagePreview, setImagePreview] = useState(null);
  const [sendOtp] = useSendOtpToDoctorMutation();
  const [registerBeautician] = useRegisterDoctorMutation();
  const [open, setOpen] = useState(false);
  const [otp, setOtp] = useState("");

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .required("Mobile is required")
      .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
    gender: Yup.string().required("Gender is required"),
    bio: Yup.string().required("Bio is required"),
    minutes_price: Yup.number().required("Price is required"),
    start_up_at: Yup.string().required("Start-Up Date is required"),
    image: Yup.string().required("Image is required"),
  });

  const initialValues = {
    name: "",
    email: "",
    mobile: "",
    gender: "male",
    bio: "",
    minutes_price: "",
    start_up_at: "",
    image: "",
  };

  const verifyOtp = async (val, values) => {
    const data = {
      name: values.name,
      email: values.email,
      mobile: values.mobile,
      image: values.image,
      otp: val,
      gender: values.gender,
      startUpAt: values.start_up_at,
      perMinutCharges: values.minutes_price,
    };

    try {
      const response = await registerBeautician(data).unwrap();

      if (response?.data) {
        setOpen(false);
        setOtp("");
        onNext(response.data);
        console.log("verify response ----", response);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Something went wrong.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.data?.message || error?.message || "Server error",
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        // onNext();
        try {
          let response = await sendOtp({ mobile: values.mobile }).unwrap();
          if (response?.otp) {
            setOpen(true);
            setOtp(response.otp);
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              error?.data?.message || "Failed to send OTP. Please try again.",
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form>
          <Box className="container py-4">
            <Typography variant="h5" gutterBottom>
              Doctor Details
            </Typography>

            <OtpModal
              open={open}
              onClose={() => setOpen(false)}
              otp={otp}
              onVerify={(enteredOtp) => verifyOtp(enteredOtp, values)}
            />

            <div className="row">
              <div className="col-md-6 mb-4">
                <InputLabel className="mb-2">Profile Image</InputLabel>
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#f9f9f9",
                  }}
                  onClick={() =>
                    document.getElementById("profile-image-input").click()
                  }
                >
                  {imagePreview ? (
                    <img
                      crossOrigin="anonymous"
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <AddIcon fontSize="large" color="action" />
                  )}
                  <input
                    type="file"
                    id="profile-image-input"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const uploadedPath = await uploadImage(file);
                        setFieldValue("image", uploadedPath);
                        setImagePreview(IMAGE_URL + uploadedPath);
                      }
                    }}
                  />
                </Box>
                {touched.image && errors.image && (
                  <Typography color="error" variant="caption">
                    {errors.image}
                  </Typography>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-4">
                <TextField
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Email"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  fullWidth
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Mobile"
                  type="tel"
                  name="mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  fullWidth
                  error={touched.mobile && Boolean(errors.mobile)}
                  helperText={touched.mobile && errors.mobile}
                />
              </div>

              <div className="col-md-6 mb-4">
                <FormControl fullWidth>
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup
                    row
                    name="gender"
                    value={values.gender}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="male"
                      control={<Radio />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio />}
                      label="Female"
                    />
                    <FormControlLabel
                      value="other"
                      control={<Radio />}
                      label="Other"
                    />
                  </RadioGroup>
                  {touched.gender && errors.gender && (
                    <Typography color="error" variant="caption">
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
              </div>

              <div className="col-md-12 mb-4">
                <TextField
                  label="Bio"
                  name="bio"
                  value={values.bio}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  error={touched.bio && Boolean(errors.bio)}
                  helperText={touched.bio && errors.bio}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Price Per Session"
                  type="number"
                  name="minutes_price"
                  value={values.minutes_price}
                  onChange={handleChange}
                  fullWidth
                  error={touched.minutes_price && Boolean(errors.minutes_price)}
                  helperText={touched.minutes_price && errors.minutes_price}
                />
              </div>

              <div className="col-md-6 mb-4">
                <TextField
                  label="Start-Up Date"
                  type="date"
                  name="start_up_at"
                  value={values.start_up_at}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={touched.start_up_at && Boolean(errors.start_up_at)}
                  helperText={touched.start_up_at && errors.start_up_at}
                />
              </div>
            </div>

            <Box display="flex" justifyContent="flex-end">
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                Next
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default Step1;
